import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useThree } from "@react-three/fiber";
import { XR, XROrigin, createXRStore } from "@react-three/xr";
import * as THREE from "three";
import { ArrowLeft, Gauge, Glasses, Loader2, Sprout } from "lucide-react";
import { api, playState } from "../../lib/api";
import { DIFFICULTY } from "../../engine/StepEngine";
import { Ground, Lights, Scenery, Sky } from "../../components/xr/SceneEnv";
import { HeroBaobab, XRCharacter } from "../../components/xr/XRActors";
import { SeedGame } from "../../components/xr/SeedGame";
import { FpsMeter } from "../../components/xr/Panel";

const FPS_PREF = "baobab.xr.fps";
const pickRate = (rates) => { const r = Array.from(rates); return r.includes(90) ? 90 : r.filter((x) => x <= 90).sort((a, b) => b - a)[0] || false; };
const store = createXRStore({ foveation: 1, frameRate: pickRate, frameBufferScaling: 1.2, offerSession: false, emulate: false, hand: false, controller: { teleportPointer: false } });
const NAYA_PROP = { character: "naya", file: "naya_prop" };

// Static scenery: render the sun shadow map a few times while assets stream in, then freeze it (baked).
const BakedShadows = () => {
  const gl = useThree((s) => s.gl);
  useEffect(() => { gl.shadowMap.autoUpdate = false; const ts = [300, 2500, 6000].map((d) => setTimeout(() => (gl.shadowMap.needsUpdate = true), d)); return () => ts.forEach(clearTimeout); }, [gl]);
  return null;
};

const World = ({ ctx, diff, fps, onDone, onPhase, wave }) => (
  <>
    <Sky /><fog attach="fog" args={["#9ED9CF", 22, 75]} />
    <Lights /><Ground /><Scenery /><HeroBaobab />
    <XRCharacter name="bao" height={1.0} position={[-1.05, 0, -2.3]} clip={wave ? "jump" : "idle"} />
    <XRCharacter name="naya" height={1.62} prop={NAYA_PROP} position={[1.15, 0, -2.6]} clip={wave} />
    <SeedGame ctx={ctx} diff={diff} onDone={onDone} onPhase={onPhase} />
    {fps && <FpsMeter />}
    <XROrigin position={[0, 0, 0]} />
  </>
);

const Support = () => {
  const [ok, setOk] = useState(null);
  useEffect(() => { navigator.xr?.isSessionSupported("immersive-vr").then(setOk).catch(() => setOk(false)) ?? setOk(false); }, []);
  return ok;
};

/** Seated WebXR Village (Meta Quest 3): the patient reaches for golden Graines de Vie with the controllers; results go to Baobab Care. */
export default function VRVillage() {
  const nav = useNavigate();
  const { sessionId, patientId } = playState.get();
  const [ctx, setCtx] = useState({ affected: "right", share: 0.6, sessionId });
  const [diff, setDiff] = useState("doux"), [fps, setFps] = useState(localStorage.getItem(FPS_PREF) === "on");
  const [phase, setPhase] = useState("intro"), [seed, setSeed] = useState(0), [sent, setSent] = useState(null), [presenting, setPresenting] = useState(false), [ready, setReady] = useState(false);
  const vr = Support();
  useEffect(() => store.subscribe((s) => setPresenting(!!s.session)), []);
  useEffect(() => {
    (async () => {
      const pid = patientId || (await api.get("/patients")).data.find((p) => p.is_demo)?.id;
      const [p, sess] = await Promise.all([api.get(`/patients/${pid}`), sessionId ? api.get(`/sessions/${sessionId}`) : Promise.resolve(null)]);
      setCtx({ pid, sessionId, affected: p.data.clinical_profile.affected_side, share: sess?.data.config.affected_leg_share ?? 0.6 });
    })().catch(() => {});
  }, [patientId, sessionId]);
  const onDone = useCallback((body) => {
    localStorage.setItem("baobab.vr.last", JSON.stringify({ at: new Date().toISOString(), ...body }));
    if (ctx.pid) api.post(`/patients/${ctx.pid}/savanna-runs`, body).then(() => setSent("ok")).catch(() => setSent("error"));
  }, [ctx.pid]);
  const onPhase = useCallback((p, i) => { setPhase(p); setSeed(i); }, []);
  const toggleFps = () => { localStorage.setItem(FPS_PREF, fps ? "off" : "on"); setFps(!fps); };
  const gl = useMemo(() => ({ antialias: true, powerPreference: "high-performance", toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.08, outputColorSpace: THREE.SRGBColorSpace }), []);

  return (
    <div className="fixed inset-0 bg-[#0A2540]" data-testid="vr-village" data-phase={phase} data-seed={seed} data-presenting={presenting}>
      <Canvas shadows dpr={[1, 1.75]} gl={gl} camera={{ position: [0, 1.18, 0.35], fov: 62, near: 0.05, far: 200 }} data-testid="vr-canvas"
        onCreated={({ gl: r, camera }) => { camera.lookAt(0, 1.05, -1.6); r.setClearColor("#0A2540"); setReady(true); }}>
        <XR store={store}>
          <World ctx={ctx} diff={diff} fps={fps} onDone={onDone} onPhase={onPhase} wave={phase === "done" ? "celebrate" : "wave"} />
        </XR>
        <BakedShadows />
      </Canvas>
      <div className={`pointer-events-none absolute inset-0 bg-[#0A2540] transition-opacity duration-1000 ${ready ? "opacity-0" : "opacity-100"}`} />
      {!presenting && <Overlay {...{ nav, vr, diff, setDiff, fps, toggleFps, phase, sent }} />}
    </div>
  );
}


const Overlay = ({ nav, vr, diff, setDiff, fps, toggleFps, phase, sent }) => (
  <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-col gap-3 p-4 sm:p-6">
    <div className="pointer-events-auto flex items-center justify-between gap-3">
      <button data-testid="vr-back" onClick={() => nav(-1)} className="flex h-11 items-center gap-2 rounded-full bg-[#0A2540]/80 px-4 text-sm font-bold text-white backdrop-blur"><ArrowLeft className="h-4 w-4" />Retour</button>
      <button data-testid="vr-fps-toggle" onClick={toggleFps} className={`flex h-11 items-center gap-2 rounded-full px-4 text-xs font-bold backdrop-blur ${fps ? "bg-[#17A2A2] text-white" : "bg-white/80 text-[#0A2540]"}`}><Gauge className="h-4 w-4" />Compteur FPS (kiné) : {fps ? "affiché" : "masqué"}</button>
    </div>
    <div className="pointer-events-auto max-w-md rounded-3xl border border-white/10 bg-[#0A2540]/85 p-5 text-white shadow-2xl backdrop-blur-xl" data-testid="vr-panel">
      <p className="text-[10px] font-extrabold tracking-[0.3em] text-[#17A2A2]">LE VILLAGE · RÉALITÉ VIRTUELLE · ASSIS</p>
      <h1 className="mt-1 font-display text-3xl font-bold">La cueillette des graines</h1>
      <p className="mt-2 text-sm text-white/75">Installe-toi sur une chaise stable, sans bouger les pieds. Tends doucement le bras vers chaque Graine de Vie dorée avec ta manette.</p>
      <div className="mt-3 flex gap-2" data-testid="vr-difficulty">
        {Object.entries(DIFFICULTY).map(([k, d]) => (
          <button key={k} data-testid={`vr-difficulty-${k}`} onClick={() => setDiff(k)} disabled={phase !== "intro"}
            className={`h-11 flex-1 rounded-full text-xs font-bold transition-colors ${diff === k ? "bg-[#E0B33A] text-[#0A2540]" : "bg-white/10 text-white"}`}>{d.label} · {d.reps * 3}</button>
        ))}
      </div>
      <button data-testid="enter-vr-button" disabled={!vr} onClick={() => store.enterVR()}
        className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#E0B33A] text-base font-extrabold text-[#0A2540] shadow-lg shadow-[#E0B33A]/30 transition-transform active:scale-[.98] disabled:opacity-50">
        {vr === null ? <Loader2 className="h-5 w-5 animate-spin" /> : <Glasses className="h-5 w-5" />}Enter VR · Entrer en VR
      </button>
      <p className="mt-2 text-xs text-white/60" data-testid="vr-support">{vr ? "Casque détecté : appuie sur « Enter VR »." : vr === false ? "Ouvre cette page dans le navigateur du Meta Quest 3 pour l'immersion. Aperçu ici : clique sur « Commencer » puis sur les graines (ou touche Espace)." : "Recherche du casque…"}</p>
      {sent && <p className="mt-2 flex items-center gap-2 text-xs font-bold text-[#5BE3B4]" data-testid="vr-sent-status"><Sprout className="h-4 w-4" />{sent === "ok" ? "Résultats envoyés à ton kiné (Baobab Care)" : "Envoi impossible, réessaie plus tard"}</p>}
    </div>
  </div>
);
